<template>
  <MainLayout>
    <!-- Orders Sub Navigation -->
    <OrdersSubNavbar />
    
    <div class="space-y-3 sm:space-y-6 px-3 sm:px-6">
      <!-- Loading State -->
      <LoadingSpinner
        v-if="loading"
        size="lg"
        color="primary"
        :show-text="false"
      />

      <!-- Error State -->
      <div
        v-else-if="error"
        class="bg-red-50 border-l-4 border-red-400 p-4"
      >
        <div class="flex">
          <div class="flex-shrink-0">
            <XMarkIcon
              class="h-5 w-5 text-red-400"
              aria-hidden="true"
            />
          </div>
          <div class="ml-3">
            <p class="text-sm text-red-700">
              {{ error }}
            </p>
            <button
              class="mt-2 text-sm font-medium text-red-700 hover:text-red-600 focus:outline-none"
              @click="() => fetchOrders()"
            >
              {{ t('app.views.orders.try_again') }} <span aria-hidden="true">&rarr;</span>
            </button>
          </div>
        </div>
      </div>

      <div v-else>
        <PageHeader :title="t('app.views.orders.title')">
          <template #actions>
            <div class="flex items-center gap-3">
              <!-- View Mode Toggle -->
              <ViewModeToggle v-model:view-mode="viewMode" />
              
              <BaseButton @click="openNewOrderModal">
                <PlusIcon
                  class="h-5 w-5"
                  aria-hidden="true"
                />
                {{ t('app.views.orders.new_order') }}
              </BaseButton>
            </div>
          </template>
        </PageHeader>
        <!-- Compact Filters Component -->
        <OrderCompactFilters
          :tabs="tabs"
          :selected-status="selectedStatus"
          :payment-filter="selectedPaymentFilter"
          :order-type="selectedOrderType"
          :table-filter="selectedTableFilter"
          :tables="tables"
          @select-tab="selectTab"
          @update:payment-filter="selectedPaymentFilter = $event" 
          @update:order-type="selectedOrderType = $event"
          @update:table-filter="selectedTableFilter = $event"
        />

        <!-- Order List Component -->
        <OrderList
          :orders="filteredOrders"
          :view-mode="viewMode"
          @new-order="openNewOrderModal"
          @view="viewOrderDetails"
          @edit="openEditOrder"
          @complete="updateOrderStatus($event, 'completed')"
          @cancel="cancelOrder"
        />

        <!-- Order Details Modal -->
        <OrderDetails
          v-if="isOrderDetailsOpen && selectedOrder"
          :open="isOrderDetailsOpen"
          :order="selectedOrder"
          @close="closeOrderDetails"
          @status-update="handleStatusUpdate"
          @payment-completed="handlePaymentCompleted"
          @edit-order="openEditOrder"
          @open-cash-register="handleOpenCashRegister"
        />

        <!-- New Order Modal - only mount when needed -->
        <NewOrderModal
          v-if="isNewOrderModalOpen"
          :open="isNewOrderModalOpen"
          :mode="newOrderMode"
          :order-to-edit="newOrderMode === 'edit' ? selectedOrderForEdit : null"
          :table-id="newOrderMode === 'create' ? undefined : (selectedOrderForEdit?.table_id ?? undefined)"
          @close="closeNewOrderModal"
          @order-created="handleNewOrder"
          @order-updated="handleOrderUpdated"
        />
      </div>
    </div>
  </MainLayout>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import MainLayout from '@/components/layout/MainLayout.vue';
import PageHeader from '@/components/layout/PageHeader.vue';
import OrdersSubNavbar from '@/components/orders/OrdersSubNavbar.vue';
import OrderCompactFilters from '@/components/orders/OrderCompactFilters.vue';
import OrderList from '@/components/orders/OrderList.vue';
import OrderDetails from '@/components/orders/OrderDetailsModal.vue';
import NewOrderModal from '@/components/orders/NewOrderModal.vue';
import { PlusIcon, XMarkIcon } from '@heroicons/vue/24/outline';
import BaseButton from '@/components/common/BaseButton.vue';
import ViewModeToggle from '@/components/common/ViewModeToggle.vue';
import LoadingSpinner from '@/components/base/LoadingSpinner.vue';
import { useOrderFilters, type PaymentFilter, type OrderTypeFilter, type TableFilter } from '@/composables/useOrderFilters';
import { useOrdersView } from '@/composables/useOrdersView';

/**
 * OrdersView - Main view for order management
 * 
 * Orchestrates order listing, filtering, creation, editing, and status management.
 * Uses composables for business logic separation following SOLID principles.
 */

// i18n
const { t } = useI18n();

// Business logic from composable
const {
  // State
  loading,
  error,
  orders,
  tables,
  selectedStatus,
  tabs,
  
  // Modal states
  isNewOrderModalOpen,
  newOrderMode,
  selectedOrderForEdit,
  isOrderDetailsOpen,
  selectedOrder,
  
  // Operations
  fetchOrders,
  fetchTables,
  selectTab,
  updateOrderStatus,
  cancelOrder,
  openNewOrderModal,
  closeNewOrderModal,
  openEditOrder,
  viewOrderDetails: viewOrderDetailsBase,
  closeOrderDetails,
  handleNewOrder,
  handleOrderUpdated,
  handleStatusUpdate,
  handlePaymentCompleted,
  handleOpenCashRegister,
  cleanup
} = useOrdersView();

// Local filter state (UI-only, not in composable)
const selectedPaymentFilter = ref<PaymentFilter>('all');
const selectedOrderType = ref<OrderTypeFilter>('all');
const selectedTableFilter = ref<TableFilter>('all');

// View mode state
const viewMode = ref<'list' | 'card'>('card');

// Use order filters composable for client-side filtering
const { filteredOrders } = useOrderFilters(
  orders,
  selectedStatus,
  selectedPaymentFilter,
  selectedOrderType,
  selectedTableFilter
);

/**
 * Wrapper for viewOrderDetails to handle nextTick
 * Required because composable can't use nextTick directly
 */
const viewOrderDetails = (order: any) => {
  viewOrderDetailsBase(order);
  nextTick(() => {
    // Ensure modal is open after DOM update
  });
};

// Initialize component
onMounted(() => {
  fetchOrders();
  fetchTables();
});

// Cleanup on unmount
onUnmounted(() => {
  cleanup();
});
</script>
